import React from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, Button, Tooltip } from '@workboard/wobo-ui'
import EditOutlined from '@material-ui/icons/EditOutlined';
import { KeyDownEvent } from '../../../../utils/eventKey';
import { ButtonsComp } from '../../../inputs/buttons.component';
import { ErrorTextComp } from '../../../inputs/fields.component';
import { krdetailConstants } from '../../../../constants/krdetails.constants';
import { getExternalAppIconClass, getExternalAppIdByKpiType, getKpiExternalAppDisplayNameById, getExternalAppIconDataUri } from './../../../../utils/kpi-market-place-util';

const KrTypeComp = props => {
  const wizard_settings = (typeof props.kr_ui_wizard !== 'undefined' && props.kr_ui_wizard.length) ? props.kr_ui_wizard : false;
  const { t, i18n } = useTranslation();
  const { enableModules = {} } = props.goalDetails || {};
  const executeModule = enableModules.executeModule || false;
  const { isChildKr = false, displayNestedKr = false, mitChildKr = false } = props;
  const { item: krItem } = props;

  // Check if KR is within a cascade (has cascade_goal_id or pkr_id)
  const isWithinCascade = props.item && props.addEditFlag && (
    (props.item.cascade_goal_id !== null && props.item.cascade_goal_id !== undefined && props.item.cascade_goal_id !== '') ||
    (props.item.pkr_id !== null && props.item.pkr_id !== undefined && props.item.pkr_id !== '')
  );
  const defaultLabels = {
    "person": ` ${t('Set a single key result target')} `,
    "survey": t("Survey a group of people"),
    "cascade": `${t('Cascade result to multiple people')}`,
    "workstream": t(`Measure progress of actions from a WorkBoard workstream`),
    "rollup": `${t('Source from other key results or objectives')}`,
    "kpi": t("Source from KPIs or external applications"),
    "multipleIntervalTargets": t(`Set multiple key result targets for the period`),
    // "multipleIntervalTargets": displayNestedKr ?
    //                               `Set quarterly ${t('key result')} targets and supporting ${t('key results')} for your annual result in one location`:
    //                               `Set quarterly ${t('key result')} targets`,
    "krstudio": t(`Microsoft key result Studio`),
    "rollupokrs": t(`Roll up key result or objectives`),
    "mirrorkr": t(`Mirror a key result`)
  };
  const buttonStates = {
    BUTTON_COMING_SOON: 'coming',
    BUTTON_DISABLED: 'disabled',
    BUTTON_ENABLED: 'enabled'
  };

  const isEnabled = id => {
    if (
      !wizard_settings ||
      (id === 'survey' && isSurveyKrEnabled())
    ) {
      return true;
    }

    const button = wizard_settings.find(b => b.id == id);
    if (typeof button !== 'undefined' && button.hasOwnProperty('value')) {
      return [
        buttonStates.BUTTON_ENABLED,
        buttonStates.BUTTON_COMING_SOON,
        'true'
      ].includes(button.value);
    }
    return false;
  };

  const isClickable = id => {
    if (
      !wizard_settings ||
      (id === 'survey' && isSurveyKrEnabled())
    ) {
      return true;
    }
    const button = wizard_settings.find(b => b.id == id);
    if (typeof button !== 'undefined' && button.hasOwnProperty('value')) {
      return [
        buttonStates.BUTTON_ENABLED,
        'true'
      ].includes(button.value);
    }
    return false;
  };

  const getText = id => {
    if (
      !wizard_settings ||
      (id === 'survey' && isSurveyKrEnabled())
    ) {
      return defaultLabels[id];
    }
    const button = wizard_settings.find(b => b.id == id);
    let label = '';
    if (typeof button !== 'undefined' && button.hasOwnProperty('name')) {
      label = button.name;
      if (button.hasOwnProperty('value') && button.value == buttonStates.BUTTON_COMING_SOON) {
        label += ' - Coming soon';
      }
    }
    return label ? label : defaultLabels[id];
  };

  /**
   * Check if survey KRs are enabled
   * Logic as of 5/16/24 should be:
   *   - If the FF 'isSurveyKrDisabled' is set to true, return false and dont show survey KR
   *
   * @returns {boolean} - returns true if the survey key result is enabled
   */
  const isSurveyKrEnabled = () => {
    if (
        wbConfig.isSurveyKrDisabled !== null &&
        wbConfig.isSurveyKrDisabled !== undefined &&
        wbConfig.isSurveyKrDisabled === true
      ) {
      return false;
    }

    return true;
  };

  const isOkrKpiIntegrationEnabled = () => {
    if (
        wbConfig.isOkrKpiIntegrationEnabled !== null &&
        wbConfig.isOkrKpiIntegrationEnabled !== undefined &&
        wbConfig.isOkrKpiIntegrationEnabled === true
      ) {
      return true;
    }

    return false;
  };

  const sourcePerson = {
    id: "person",
    text: getText("person"),
    clickable: isClickable("person"),
    subText: t(`Measure results like sales or bugs, and assign the key result to a point person`),
    iconClass: "icon icon-number-sign-24px-bluegreen"
  };

  const sourceSurvey = {
    id: "survey",
    text: getText("survey"),
    clickable: isClickable("survey"),
    subText: t("Run a group survey to get multiple sources of data"),
    iconClass: "icon icon-yes-no-24px-bluegreen"
  };

  const sourceCascade = {
    id: "cascade",
    text: getText("cascade"),
    clickable: isClickable("cascade"),
    subText: t(`Delegate key results to others to create that roll up to this overarching key result`),
    iconClass: "icon icon-cascade-24px-bluegreen"
  };

  const sourceWorkstream = {
    id: "workstream",
    text: props.isZenry ? "Zenry workstreams" : getText("workstream"),
    clickable: isClickable("workstream"),
    subText: t("Measure percentage completion of actions for a group of people"),
    iconClass: "icon icon-outline-waves-24px-bluegreen"
  };

  const sourceRollup = {
    id: "rollup",
    text: getText("rollup"),
    clickable: isClickable("rollup"),
    subText: t('Link directly and mirror a key result or roll up multiple existing key results or objectives'),
    iconClass: "icon icon-outline-bar_chart-24px-bluegreen"
  };

  const sourceKPI = {
    id: "kpi",
    text: getText("kpi"),
    clickable: false,
    subText: t("Automate from sales, development, R&D, operations systems or KPIs"),
    iconClass: "integration-icon-24px-bluegreen"
  };

  const sourcesRollup = {
    id: "rollupokrs",
    text: getText("rollupokrs"),
    clickable: isClickable("rollupokrs"),
    subText: t("Pull data from one or multiple key results or objectives "),
    iconClass: "icon icon-outline-bar_chart-24px-bluegreen"
  };

  const sourcesMirror = {
    id: "mirrorkr",
    text: getText("mirrorkr"),
    clickable: isClickable("mirrorkr"),
    subText: t("Pull data directly from another key result"),
    iconClass: "icon icon-outline-link-24px-bluegreen"
  };

  const sourceMIT = {
    id: "multipleIntervalTargets",
    text: getText("multipleIntervalTargets"),
    clickable: isWithinCascade ? false : isClickable("multipleIntervalTargets"),
    subText: t(`Set quarterly targets for an annual key result or monthly targets for a quarterly key result`),
    iconClass: "icon icon-outline-timeline-24px-bluegreen",
    disabled: isWithinCascade,
    disabledTooltip: isWithinCascade ? t(`Multiple Interval Targets on Cascades are not yet supported`) : null
  };


  const sourceKrstudio = {
    id: "krstudio",
    text: getText("krstudio"),
    clickable: isClickable("krstudio"),
    subText: t("Utilize data from internal Microsoft data sources"),
    iconClass: "icon icon-microsoft-24px"
  };

  const rollupButtons = [{
    id: "mirrorkr",
    label: t('Mirror one key result'),
    ariaDescribedref:"KrTypeSourceLabel",
    iconClass: "icon icon-outline-link-24px-bluegreen",
    buttonWrapperClass: "",
    buttonClick: props.clickKrTypeText,
    addEditFlag: props.addEditFlag,
  }, {
    id: "rollupokrs",
    label: t('Roll-up key results or objectives'),
    ariaDescribedref:"KrTypeSourceLabel",
    iconClass: "icon icon-outline-bar_chart-24px-bluegreen",
    buttonWrapperClass: "",
    buttonClick: props.clickKrTypeText
  }];

  let kpiButtons = [];

  if (isOkrKpiIntegrationEnabled()) {
    kpiButtons.push({
      id: "kpi-modal",
      label: t('KPIs'),
      ariaDescribedref:"KrTypeSourceLabel",
      iconClass: "icon-kpi-pulse-bluegreen-24px",
      buttonWrapperClass: "",
      buttonClick: props.clickKrTypeText,
    });
  }

  kpiButtons.push({
    id: "kpi",
    label: t('External data sources'),
    ariaDescribedref:"KrTypeSourceLabel",
    iconClass: "icon-external-datasource-icons",
    buttonWrapperClass: "",
    buttonClick: props.clickKrTypeText
  });

  let manualSources = [
    sourcePerson,
    sourceMIT
  ];

  // add separately since the survey source is not always enabled
  let extraSources = [];
  if (isSurveyKrEnabled()) {
    extraSources.push(sourceSurvey, sourceCascade);
  } else {
    extraSources.push(sourceCascade);
  }

  let autoSources = [
    sourceRollup,
    sourceKPI
  ];

  if(mitChildKr){
    autoSources = [
      sourceKPI,
      sourcesRollup,
      sourcesMirror
    ];
  }

  if (executeModule) {
    autoSources.unshift(sourceWorkstream);
  }

  if (wizard_settings) {
    manualSources = [];
    extraSources = [];
    if (isEnabled('person')) {
      manualSources.push(sourcePerson);
    }
    if (isSurveyKrEnabled() && isEnabled('survey')) {
      extraSources.push(sourceSurvey);
    }
    if (isEnabled('cascade')) {
      extraSources.push(sourceCascade);
    }
    autoSources = [];
    if (isEnabled('kpi')) {
      autoSources.push(sourceKPI);
    }
    if (executeModule && isEnabled('workstream')) {
      autoSources.push(sourceWorkstream);
    }
    if (isEnabled('krstudio')) {
      autoSources.push(sourceKrstudio);
    }
    if (isEnabled('rollup')) {
      autoSources.push(sourceRollup);
    }
    if (isEnabled('multipleIntervalTargets')) {
      manualSources.push(sourceMIT);
    }
  }

  if (isChildKr) {
    manualSources = manualSources.filter(s => s.id != 'multipleIntervalTargets');
    extraSources = extraSources.filter(s => s.id != 'cascade');
  }

  if (mitChildKr) {
    manualSources = manualSources.filter(s => s.id == 'person');
    autoSources = autoSources.filter(s => s.id == 'kpi' || s.id == 'rollupokrs'|| s.id == 'mirrorkr' || s.id == "workstream");
    extraSources = extraSources.filter(s => s.id == '');
  }
  const sources = [
    {
      name: '',
      items: manualSources
    },
    {
      name: t('AUTOMATE OR ROLL UP RESULTS'),
      items: autoSources
    },
    ...(extraSources.length > 0
      ? [{
          name: t('COLLECT DATA FROM MULTIPLE PEOPLE'),
          items: extraSources
        }]
      : [])
  ];

  const errors = props.errors || {};
  let selectedMetric = '';
  let krType = props.krType;

  if (props.krType === "mirrorkr") {
    selectedMetric = { ...sourcesMirror };
    krType = "mirrorkr";
  } else if (krType !== '') {
    if (!mitChildKr && krType === "rollupokrs") {
      krType = "rollup";
    }
    if (mitChildKr && krType === "rollupokrs") {
      krType = "rollupokrs";
    }
    sources.map(source => {
      source.items.map(item => {
        if (item.id == krType) {
          selectedMetric = item;
          if (krType === "rollup") {
            item.text = t("Roll-up key results or objectives");
          } else if (krType === "krstudio") {
            item.text = `${t('Result from ')}` + item.text;
          } else if (krType === 'kpi') {
            if (props.kpiType === 'wobo_kpi') {
              // Internal KPI
              item.iconClass = 'icon-kpi-pulse-bluegreen-24px';
              item.text = t('KPI');
            } else if (props.kpiType) {
              const ext_app_id = getExternalAppIdByKpiType(props.kpiType);
              item.iconClass = getExternalAppIconClass(ext_app_id) || item.iconClass;
              item.text = getKpiExternalAppDisplayNameById(ext_app_id) || item.text;
              item.dataUri = getExternalAppIconDataUri(ext_app_id);
            } else {
              item.iconClass = getExternalAppIconClass(props.item.ext_app_id) || item.iconClass;
              item.text = getKpiExternalAppDisplayNameById(props.item.ext_app_id) || item.text;
              item.dataUri = getExternalAppIconDataUri(props.item.ext_app_id);
            }
          }
        }
      });
    });
  }
  const selectKrTypeKeyEvent = (item, e)=>{
    props.clickKrTypeText(item,e)
  }

  return (
    <div className="aEKrContent">
      <div className="wherekrDataCome">
        <span  role="heading" aria-level="2">{t('How will you measure your key result?')} <span className="typefacetext">*</span></span>
        <ErrorTextComp
          errors={errors}
          field="krType"
          errorClass="krtype-error-text"
        />
      </div>
      {props.showChangeOption ? (
        <div className="width100 flex-row-left">
          <div className="width50 selected-metric-option">
            { !selectedMetric.dataUri && <div className={"selected-metric-icon icon-" + krType + (selectedMetric ? " " + selectedMetric.iconClass : "")}/>}
            { selectedMetric.dataUri && <img src={selectedMetric.dataUri} style={{width: "20px", "margin-right": "12px"}}/>}

            <div className="selected-metric-label" data-testid="selected-metric-label">
              {selectedMetric ? selectedMetric.text : ""}
            </div>
          </div>
          <div className="width50" data-testid="button-comp-label">
            <ThemeProvider>
              <Button
                variant="secondary"
                startIcon={<EditOutlined />}
                onClick={()=>
                  {
                    if(props.mitChildKr){
                      props.updateKRDataObject();
                    } else {
                      props.changeDataSourceOption();
                    }
                    setTimeout(()=>{
                      document.getElementById("set-delete-focus")?.focus();
                    })
                    setTimeout(()=>{
                      if(document.getElementById("set-arrowbtn-focus") != null)
                      {
                        document.getElementById("set-arrowbtn-focus").focus();
                      }
                      })
                  }
                }
                className="measurementTypeBtn"
                isDisabled={Number(krItem?.status) === krdetailConstants.METRIC_CLOSED}
                id="change-measurement"
                data-testid="button-comp-label">
                {t('Change measurement type')}
              </Button>
            </ThemeProvider>
          </div>
        </div>
      ) : (
          <div className="kr-types-wrapper">
            {sources.map((source, ind) => (
              <div key={ind}>
                { (source.name != '') && <div role="heading" aria-level="2" className="manuallyENt">{source.name}</div> }
                {source.items.map(item => (
                  (item.id != "rollup") ?
                  (item.disabled && item.disabledTooltip ? (
                    <Tooltip key={item.id} content={item.disabledTooltip} placement="top" arrow compact PopperProps={{ disablePortal: true }}>
                      <div
                        onKeyDown={(e) => !item.disabled && KeyDownEvent(e, () => selectKrTypeKeyEvent(item,e))}
                        role="button"
                        tabIndex={item.disabled ? -1 : 0}
                        className={`krTypes ${item.disabled ? 'krTypes-disabled' : 'cursor-pointer'}`}
                        onClick={(e) => !item.disabled && props.clickKrTypeText(item,e)}
                        style={item.disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                      >
                        <div className="subKrTypeHead">
                          <div className={"krTypeIcon " + item.iconClass} />
                          {item.id != "rollup" ? (
                            <div className={`KrTypeText ${item.disabled ? '' : 'cursor-pointer'}`}  data-testid={item.id}>
                              {item.text}
                            </div>
                          ) : (
                            <div className="KrTypeText" >
                              {item.text}
                            </div>
                          )}
                        </div>
                        <div className="krTypesInfotext">{item.subText} </div>
                        {item.id == "rollup" ? (<ButtonsComp buttons={rollupButtons} addEditFlag={props.addEditFlag} />) : ( "" )}
                        {item.id == "kpi" ? (<ButtonsComp buttons={kpiButtons} addEditFlag={props.addEditFlag} />) : ( "" )}
                      </div>
                    </Tooltip>
                  ) : (
                    <div
                      key={item.id}
                      onKeyDown={(e) => !item.disabled && KeyDownEvent(e, () => selectKrTypeKeyEvent(item,e))}
                      role="button"
                      tabIndex={item.disabled ? -1 : 0}
                      className={`krTypes ${item.disabled ? 'krTypes-disabled' : 'cursor-pointer'}`}
                      onClick={(e) => !item.disabled && props.clickKrTypeText(item,e)}
                      style={item.disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                    >
                      <div className="subKrTypeHead">
                        <div className={"krTypeIcon " + item.iconClass} />
                        {item.id != "rollup" ? (
                          <div className={`KrTypeText ${item.disabled ? '' : 'cursor-pointer'}`}  data-testid={item.id}>
                            {item.text}
                          </div>
                        ) : (
                          <div className="KrTypeText" >
                            {item.text}
                          </div>
                        )}
                      </div>
                      <div className="krTypesInfotext">{item.subText} </div>
                      {item.id == "rollup" ? (<ButtonsComp buttons={rollupButtons} addEditFlag={props.addEditFlag} />) : ( "" )}
                      {item.id == "kpi" ? (<ButtonsComp buttons={kpiButtons} addEditFlag={props.addEditFlag} />) : ( "" )}
                    </div>
                  )) :
                  <div key={item.id} className={"krTypes "}>
                  <div className="subKrTypeHead">
                    <div className={"krTypeIcon " + item.iconClass} />
                    {item.id != "rollup" ? (
                      <div
                        className="KrTypeText cursor-pointer"

                        onClick={() => props.clickKrTypeText(item)}
                      >
                        {item.text}
                      </div>
                    ) : (
                      <div id="KrTypeSourceLabel" className="KrTypeText " >{item.text}</div>
                    )}
                  </div>
                  <div className="krTypesInfotext">{item.subText} </div>
                  {item.id == "rollup" ? (<ButtonsComp buttons={rollupButtons} addEditFlag={props.addEditFlag}/>) : ( "" )}
                </div>
                ))}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default KrTypeComp;
